import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Users, UserPlus, UserX, Search, Eye, X, Check, Bell } from 'lucide-react';
import SubscriptionBanner from '@/components/SubscriptionBanner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Friends = () => {
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    loadFriends();
    loadRequests();
  }, []);

  const loadFriends = async () => {
    try {
      const response = await axios.get(`${API}/friends`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setFriends(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load friends');
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    try {
      const response = await axios.get(`${API}/friends/requests`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to load requests');
    }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 2) {
      toast.error('Search query too short');
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`${API}/users/search?query=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleSendRequest = async (email) => {
    try {
      await axios.post(
        `${API}/friends/request?friend_email=${encodeURIComponent(email)}`,
        {},
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      toast.success('Friend request sent!');
      setSearchResults([]);
      setSearchQuery('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send request';
      toast.error(message);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await axios.put(
        `${API}/friends/${requestId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      toast.success('Friend request accepted!');
      loadFriends();
      loadRequests();
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await axios.put(
        `${API}/friends/${requestId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      toast.success('Friend request rejected');
      loadRequests();
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (!window.confirm('Remove this friend?')) return;

    try {
      await axios.delete(`${API}/friends/${friendId}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      toast.success('Friend removed');
      loadFriends();
    } catch (error) {
      toast.error('Failed to remove friend');
    }
  };

  const viewProfile = (userId) => {
    navigate(`/profile/${userId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-bg">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg">
      <SubscriptionBanner />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl heading-font font-bold tracking-tight mb-2 text-white" data-testid="friends-title">
            Friends
          </h1>
          <p className="text-gray-400 body-font">Manage your connections and view friends' schedules</p>
        </div>

        {/* Friend Requests - now shown in notification bell */}
        {requests.length > 0 && (
          <div className="dark-card p-4 mb-6 border-l-4 border-teal-400 bg-teal-500/5">
            <p className="text-sm text-teal-400">
              <Bell className="inline w-4 h-4 mr-2" />
              You have {requests.length} pending friend request{requests.length > 1 ? 's' : ''}. 
              <span className="font-semibold"> Check the notification bell to respond!</span>
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Search Users */}
          <div className="dark-card p-6">
            <h2 className="text-2xl heading-font font-bold mb-4 text-white" data-testid="search-users-title">
              Find Friends
            </h2>
            <div className="flex gap-2 mb-4">
              <Input
                placeholder="Search by name or email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-teal-400"
                data-testid="search-input"
              />
              <Button
                onClick={handleSearch}
                disabled={searching}
                className="teal-btn"
                data-testid="search-btn"
              >
                <Search size={18} />
              </Button>
            </div>

            {searchResults.length > 0 && (
              <div className="space-y-2">
                {searchResults.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 border border-white/10 rounded-xl"
                    data-testid={`search-result-${user.id}`}
                  >
                    <div>
                      <p className="font-semibold body-font text-white">{user.name}</p>
                      <p className="text-sm text-gray-400 body-font">{user.email}</p>
                    </div>
                    <Button
                      size="sm"
                      className="teal-btn"
                      onClick={() => handleSendRequest(user.email)}
                      data-testid={`send-request-${user.id}`}
                    >
                      <UserPlus size={16} className="mr-1" /> Add
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Friends List */}
          <div className="dark-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl heading-font font-bold text-white" data-testid="friends-list-title">
                My Friends ({friends.length})
              </h2>
            </div>

            {friends.length === 0 ? (
              <p className="text-center text-gray-400 py-8 body-font" data-testid="no-friends">
                No friends yet. Search and add friends to get started!
              </p>
            ) : (
              <div className="space-y-2">
                {friends.map((friend) => (
                  <div
                    key={friend.id}
                    className="flex items-center justify-between p-3 border border-white/10 rounded-xl hover:border-teal-400/30 transition-snap"
                    data-testid={`friend-${friend.id}`}
                  >
                    <div className="flex-1">
                      <p className="font-semibold body-font text-white">{friend.name}</p>
                      <p className="text-sm text-gray-400 body-font">{friend.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-white/10 text-gray-400 hover:text-white hover:border-white/20"
                        onClick={() => viewProfile(friend.id)}
                        data-testid={`view-profile-${friend.id}`}
                      >
                        <Eye size={16} className="mr-1" /> View
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={() => handleRemoveFriend(friend.id)}
                        data-testid={`remove-friend-${friend.id}`}
                      >
                        <UserX size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Friends;